/**
 * 视频筛选数据管理模块
 * 用于存储和管理视频列表的筛选条件
 */

const state = {
  // 筛选条件数据
  filterData: {},
  // 是否已初始化
  isInitialized: false,
  // 筛选历史记录
  filterHistory: [],
  // 品牌型号相关数据
  brandModelData: {
    brandId: '',
    modelIds: '',
    selectedModelCount: 0
  }
}

const mutations = {
  /**
   * 设置筛选数据
   * @param {Object} state - Vuex state
   * @param {Object} filterData - 筛选数据对象
   */
  SET_FILTER_DATA(state, filterData) {
    state.filterData = { ...state.filterData, ...filterData }
  },

  /**
   * 清空筛选数据
   * @param {Object} state - Vuex state
   */
  CLEAR_FILTER_DATA(state) {
    state.filterData = {}
  },

  /**
   * 重置特定筛选条件
   * @param {Object} state - Vuex state
   * @param {String} key - 要重置的筛选条件key
   */
  RESET_FILTER_KEY(state, key) {
    if (state.filterData[key]) {
      delete state.filterData[key]
    }
  },

  /**
   * 设置初始化状态
   * @param {Object} state - Vuex state
   * @param {Boolean} isInitialized - 是否已初始化
   */
  SET_INITIALIZED(state, isInitialized) {
    state.isInitialized = isInitialized
  },

  /**
   * 添加筛选历史记录
   * @param {Object} state - Vuex state
   * @param {Object} filterData - 筛选数据
   */
  ADD_FILTER_HISTORY(state, filterData) {
    // 限制历史记录数量，最多保存10条
    if (state.filterHistory.length >= 10) {
      state.filterHistory.shift()
    }
    state.filterHistory.push({
      ...filterData,
      timestamp: Date.now()
    })
  },

  /**
   * 清空筛选历史记录
   * @param {Object} state - Vuex state
   */
  CLEAR_FILTER_HISTORY(state) {
    state.filterHistory = []
  },

  /**
   * 更新品牌型号数据
   * @param {Object} state - Vuex state
   * @param {Object} brandModelData - 品牌型号数据
   */
  UPDATE_BRAND_MODEL_DATA(state, brandModelData) {
    state.brandModelData = { ...state.brandModelData, ...brandModelData }
  },

  /**
   * 清空品牌型号数据
   * @param {Object} state - Vuex state
   */
  CLEAR_BRAND_MODEL_DATA(state) {
    state.brandModelData = {
      brandId: '',
      modelIds: '',
      selectedModelCount: 0
    }
  }
}

const actions = {
  /**
   * 更新筛选数据
   * @param {Object} context - Vuex context
   * @param {Object} filterData - 筛选数据
   */
  updateFilterData ({ commit }, filterData) {
    commit('SET_FILTER_DATA', filterData)
    commit('ADD_FILTER_HISTORY', filterData)
  },

  /**
   * 重置所有筛选条件
   * @param {Object} context - Vuex context
   */
  resetAllFilters({ commit }) {
    commit('CLEAR_FILTER_DATA')
  },

  /**
   * 重置特定筛选条件
   * @param {Object} context - Vuex context
   * @param {String} key - 要重置的筛选条件key
   */
  resetFilterKey({ commit }, key) {
    commit('RESET_FILTER_KEY', key)
  },

  /**
   * 初始化筛选数据
   * @param {Object} context - Vuex context
   * @param {Object} initialData - 初始数据
   */
  initializeFilterData({ commit }, initialData = {}) {
    commit('SET_FILTER_DATA', initialData)
    commit('SET_INITIALIZED', true)
  },

  /**
   * 更新品牌型号数据
   * @param {Object} context - Vuex context
   * @param {Object} brandModelData - 品牌型号数据
   */
  updateBrandModelData({ commit }, brandModelData) {
    commit('UPDATE_BRAND_MODEL_DATA', brandModelData)
  },

  /**
   * 清空品牌型号数据
   * @param {Object} context - Vuex context
   */
  clearBrandModelData({ commit }) {
    commit('CLEAR_BRAND_MODEL_DATA')
  }
}

const getters = {
  /**
   * 获取所有筛选数据
   * @param {Object} state - Vuex state
   * @returns {Object} 筛选数据
   */
  getFilterData: (state) => state.filterData,

  /**
   * 获取特定筛选条件
   * @param {Object} state - Vuex state
   * @returns {Function} 返回获取特定筛选条件的函数
   */
  getFilterByKey: (state) => (key) => state.filterData[key] || '',

  /**
   * 检查是否有筛选条件
   * @param {Object} state - Vuex state
   * @returns {Boolean} 是否有筛选条件
   */
  hasFilters: (state) => Object.keys(state.filterData).length > 0,

  /**
   * 获取筛选历史记录
   * @param {Object} state - Vuex state
   * @returns {Array} 筛选历史记录
   */
  getFilterHistory: (state) => state.filterHistory,

  /**
   * 获取初始化状态
   * @param {Object} state - Vuex state
   * @returns {Boolean} 是否已初始化
   */
  isInitialized: (state) => state.isInitialized,

  /**
   * 获取品牌型号数据
   * @param {Object} state - Vuex state
   * @returns {Object} 品牌型号数据
   */
  getBrandModelData: (state) => state.brandModelData,

  /**
   * 获取选中的型号数量
   * @param {Object} state - Vuex state
   * @returns {Number} 选中的型号数量
   */
  getSelectedModelCount: (state) => state.brandModelData.selectedModelCount,
  getSelectedBrandModelData: (state) => state.brandModelData
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
